import _merge from 'lodash/merge';

import { formatter } from '../utils';

/**
 * @param {{
 * listKey?: string,
 * title?: string,
 * list?: { percentage: string, title: string, link: string }[] | any[],
 * comparingData?: object | null,
 * listClassName?: string,
 * progressBarClassName?: string,
 * progressBarValueType?: string,
 * childNodes?: object,
 * maxHiddenItemsSize?: number
 * }}
 */
const setupHorizontalChartList = ({
  listKey = '',
  title = '',
  list = [],
  listClassName = '',
  progressBarClassName = '',
  progressBarValueType = '%',
  childNodes = null,
  maxHiddenItemsSize = 3,
  customShowMoreBtn = null,
}) => ({
  listKey,
  title,
  list,
  listClassName,
  progressBarClassName,
  progressBarValueType,
  childNodes,
  maxHiddenItemsSize,
  customShowMoreBtn,
});

/**
 * @param {String} listKey list key for comparing feature
 * @param {Array} list array of comparing items for chart list
 */
const setupHorizontalChartBarComparingList = ({ listKey = '', list = [] }) => ({
  listKey,
  list,
});

/**
 * @param {{
 * originalTitle?: string,
 * originalLogo?: string,
 * comparingTitle?: string,
 * comparingLogo?: string,
 * comparingLists?: { name: string, percentage: string }[]
 * }}
 */
const setupHorizontalChartBarComparingData = ({
  originalTitle = '',
  originalLogo = null,
  comparingTitle = '',
  comparingLogo = null,
  comparingLists = null,
}) => ({
  originalTitle,
  originalLogo,
  comparingTitle,
  comparingLogo,
  comparingLists,
});

/**
 * @param {{
 * title?: string,
 * description?: string,
 * isContentBlurb?: boolean,
 * filterGroup?: string,
 * updateFilter?: () => void,
 * chartLists?: any[],
 * showMoreButtonCallback?: () => void,
 * chartColors?: string[]
 * }}
 */
const setupHorizontalChartBar = ({
  title = '',
  description = '',
  isContentBlurb = false,
  filterGroup = '',
  newFilter = false,
  filters = [],
  onApply = null,
  updateFilter = null,
  chartLists = [],
  showMoreButtonCallback = null,
  chartColors = ['#26A69A', '#5C6BC0', '#AB47BC'],
  comparingData = null,
}) => ({
  title,
  description,
  isContentBlurb,
  filterGroup,
  newFilter,
  filters,
  onApply,
  updateFilter,
  chartLists,
  showMoreButtonCallback,
  chartColors,
  comparingData,
});

/**
 * @param {String} title salary range bar title
 * @param {String} link salary range bar link | optional
 * @param {Function} onLinkClick callback function for handle link click if needed
 * @param {Boolean} openLinkInNewTab target _blank or _self
 * @param {String} fillColor color for salary range bar
 * @param {Number} median median value of salary item
 * @param {String} medianValueDisplay median value display formatted
 * @param {String} medianValueFormatted median value display formatted for mobile view
 */
const setupSalaryRangeBarListItem = ({
  title = '',
  link = '',
  isIndexable = true,
  onLinkClick = null,
  openLinkInNewTab = false,
  fillColor = '#26A69A',
  mobileColor,
  median,
  medianValueDisplay,
  medianValueFormatted,
  percent25,
  percent75,
}) => ({
  title,
  link,
  isIndexable,
  onLinkClick,
  openLinkInNewTab,
  fillColor,
  mobileColor,
  median,
  medianValueDisplay,
  medianValueFormatted,
  percent25,
  percent75,
});

/**
 * @param {String} title salary range bar section title | optional
 * @param {String} titleClassName salary range bar title classname | optional
 * @param {String} description salary range bar description | optional
 * @param {string} filterGroup filter froup for ZpFilters inside salary range bar section | optional
 * @param {Function} updateFilter callback for ZpFilters update | optional
 * @param {Array} salaryItems salary item list
 * @param {String} showMoreButtonText show more button text | optional
 * @param {String} columnConfig column config for sorting on mobile view
 * @param {Number} maxHiddenItemsSize max size when list is hidden, default = 6
 */
const setupSalaryRangeChart = ({
  title = '',
  titleClassName = '',
  descriptionClassName = '',
  description = '',
  isContentBlurb = false,
  filterGroup = '',
  updateFilter = null,
  onClear = null,
  salaryItems = [],
  showMoreButtonText = '',
  columnConfig = null,
  maxHiddenItemsSize = 6,
  seeMoreLink = null,
  filter = null,
}) => ({
  title,
  titleClassName,
  descriptionClassName,
  description,
  isContentBlurb,
  filterGroup,
  updateFilter,
  onClear,
  salaryItems,
  showMoreButtonText,
  columnConfig,
  maxHiddenItemsSize,
  seeMoreLink,
  filter,
});


function renderChartTooltip(tooltipModel) {
  // Tooltip Element
  let tooltipEl = document.getElementById('chartjs-tooltip');

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.id = 'chartjs-tooltip';
    tooltipEl.innerHTML = '<table></table>';
    document.body.appendChild(tooltipEl);
  }

  // Hide if no tooltip
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove(...tooltipEl.classList);
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  }
  if (tooltipModel.xAlign) {
    tooltipEl.classList.add(tooltipModel.xAlign);
  }

  function getBody(bodyItem) {
    return bodyItem.lines;
  }

  // Set Text
  if (tooltipModel.body) {
    const titleLines = tooltipModel.title || [];
    const bodyLines = tooltipModel.body.map(getBody);

    let innerHtml = '<thead>';

    titleLines.forEach(function (title) {
      innerHtml += '<tr><th>' + title + '</th></tr>';
    });
    innerHtml += '</thead><tbody>';

    bodyLines.forEach(function (body, i) {
      const colors = tooltipModel.labelColors[i];
      let style = 'background:' + colors.backgroundColor;
      const span = '<span style="' + style + '" class="tooltip-key-color"></span>';
      innerHtml += '<tr><td>' + span + body + '</td></tr>';
    });
    innerHtml += '</tbody>';

    const tableRoot = tooltipEl.querySelector('table');
    tableRoot.innerHTML = innerHtml;
  }

  // `this` will be the overall tooltip
  const position = this._chart.canvas.getBoundingClientRect();

  // set display and position styles
  tooltipEl.style.opacity = 1;
  tooltipEl.style.position = 'absolute';
  tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
  tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
  tooltipEl.style.pointerEvents = 'none';
}

const defaultLineChartOptions = () => ({
  title: {
    display: false,
  },
  legend: {
    display: false,
  },
  layout: {
    padding: {
      left: 0,
      right: 0,
      top: -20,
      bottom: 0,
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  showCustomLegend: true,
  onResize: () => null,
  showTooltips: true,
  elements: {
    line: {
      tension: 0,
    },
  },
  tooltips: {
    mode: 'index',
    position: 'nearest',
    intersect: false,
    enabled: false,
    xPadding: 10,
    yPadding: 8,
    titleFontSize: 16,
    bodyFontSize: 13,
    callbacks: null,
    custom: renderChartTooltip,
  },
  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: false,
          maxTicksLimit: 3,
          fontColor: '#333333',
          fontFamily: 'Open Sans',
          fontSize: 14,
          padding: 14,
          callback(value) {
            return formatter(true).format(value).toLocaleLowerCase();
          },
        },
        gridLines: {
          display: true,
          drawBorder: false,
          zeroLineColor: 'rgba(214, 217, 219, 0.5)',
          color: 'rgba(214, 217, 219, 0.5)',
        },
      },
    ],
    xAxes: [
      {
        ticks: {
          xMin: 3,
          maxTicksLimit: 3,
          fontColor: 'rgba(51, 51, 51, 0.8)',
          fontFamily: 'Open Sans',
          fontSize: 14,
          padding: 10,
          callback(value) {
            return `${value}`;
          },
        },
        gridLines: {
          display: false,
        },
      },
    ],
  },
});

const setupLineChart = ({
  labels = null,
  datasets = null,
  options = null,
  className = '',
  width = 100,
  height = 400,
}) => ({
  labels,
  datasets,
  options: {
    ..._merge(defaultLineChartOptions(), options),
  },
  className,
  width,
  height,
});

export const ChartService = {
  setupHorizontalChartList,
  setupHorizontalChartBarComparingList,
  setupHorizontalChartBarComparingData,
  setupHorizontalChartBar,
  setupSalaryRangeBarListItem,
  setupSalaryRangeChart,
  setupLineChart,
};
